// src/services/authService.js
import { auth, googleProvider } from '../config/firebase';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  createUserWithEmailAndPassword,
  updateProfile,
  fetchSignInMethodsForEmail,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { cookieService } from './cookieService';
import { API_CONFIG } from '../config/config';

/**
 * Helper function to establish HttpOnly cookie session after Firebase login.
 * This sends the Firebase tokens to the backend which sets them as HttpOnly cookies.
 */
async function establishCookieSession(idToken, refreshToken) {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/auth/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Important: send/receive cookies
      body: JSON.stringify({
        id_token: idToken,
        refresh_token: refreshToken,
      }),
    });
    
    if (!response.ok) {
      console.warn('[AuthService] Failed to establish HttpOnly cookie session');
      return false;
    }
    
    const data = await response.json();
    return data.success;
  } catch (error) {
    console.warn('[AuthService] Error establishing cookie session:', error);
    // Don't throw - we still have localStorage fallback
    return false;
  }
}

export const authService = {
  /**
   * Connexion avec email/mot de passe
   */
  login: async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    const idToken = await user.getIdToken(false);
    const refreshToken = userCredential._tokenResponse?.refreshToken || "FAKE_REFRESH_TOKEN";
    
    // Establish HttpOnly cookie session
    await establishCookieSession(idToken, refreshToken);
    cookieService.setUserId(user.uid);
    
    return {
      firebaseUserId: user.uid,
    };
  },

  /**
   * Récupère les méthodes de connexion disponibles pour un email
   */
  getSignInMethods: async (email) => {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      return methods; // ex: ["google.com"], ["password"], []
    } catch (error) {
      console.error('[AuthService] fetchSignInMethodsForEmail failed:', error);
      throw error;
    }
  },

  /**
   * Envoie un email de réinitialisation du mot de passe
   */
  resetPassword: async (email) => {
    try {
      // ActionCodeSettings optionnels pour améliorer la délivrabilité et l'UX
      const redirectUrl = process.env.REACT_APP_FIREBASE_RESET_REDIRECT_URL || (typeof window !== 'undefined' ? window.location.origin : undefined);
      const actionCodeSettings = redirectUrl
        ? { url: redirectUrl, handleCodeInApp: false }
        : undefined;
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
    } catch (error) {
      console.error('[AuthService] sendPasswordResetEmail failed:', error);
      throw error;
    }
  },

  /**
   * Inscription avec email/mot de passe et KYC minimal (prénom et nom)
   */
  register: async ({ email, password, displayName }) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    const resolvedDisplayName = (displayName || '').trim() || email;
    // Mise à jour du profil utilisateur avec l'alias public
    await updateProfile(user, { displayName: resolvedDisplayName });
    const idToken = await user.getIdToken(false);
    const refreshToken = userCredential._tokenResponse?.refreshToken || "FAKE_REFRESH_TOKEN";
    
    // Establish HttpOnly cookie session
    await establishCookieSession(idToken, refreshToken);
    cookieService.setUserId(user.uid);
    
    return {
      firebaseUserId: user.uid,
    };
  },

  /**
   * Connexion avec Google
   */
  loginWithGoogle: async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const idToken = await user.getIdToken(false);
      const refreshToken = result._tokenResponse?.refreshToken || "FAKE_REFRESH_TOKEN";
      
      // Establish HttpOnly cookie session
      await establishCookieSession(idToken, refreshToken);
      cookieService.setUserId(user.uid);
      
      return {
        firebaseUserId: user.uid,
      };
    } catch (error) {
      console.error("[AuthService] Google Login failed:", error);
      throw error;
    }
  },

  /**
   * Inscription avec Google (similaire à loginWithGoogle)
   */
  registerWithGoogle: async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const idToken = await user.getIdToken(false);
      const refreshToken = result._tokenResponse?.refreshToken || "FAKE_REFRESH_TOKEN";
      
      // Establish HttpOnly cookie session
      await establishCookieSession(idToken, refreshToken);
      cookieService.setUserId(user.uid);
      
      return {
        firebaseUserId: user.uid,
      };
    } catch (error) {
      console.error("[AuthService] Google Registration failed:", error);
      throw error;
    }
  },

  loginWithCustomToken: async () => {
    return;
  },

  /**
   * Déconnexion : Firebase + clear HttpOnly cookies + localStorage
   */
  logout: async () => {
    // Clear HttpOnly cookies via backend
    try {
      await fetch(`${API_CONFIG.BASE_URL}/auth/session/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.warn('[AuthService] Failed to clear server cookies:', error);
    }
    
    // Firebase signout
    await signOut(auth);
    
    // Clear localStorage
    cookieService.clearAll();
  },

  /**
   * Rafraîchissement du token
   * First tries HttpOnly cookie refresh via backend, then falls back to Firebase SDK
   */
  refreshToken: async () => {
    try {
      // Try backend cookie refresh first
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/session/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          return null; // Cookie refreshed successfully
        }
      }
    } catch (error) {
      console.warn('[AuthService] Backend cookie refresh failed, trying Firebase SDK:', error);
    }
    
    // Fallback to Firebase SDK refresh
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('No current user in Firebase Auth');
      const newIdToken = await currentUser.getIdToken(true);
      
      // Re-establish HttpOnly cookie session with fresh token
      await establishCookieSession(newIdToken, currentUser.refreshToken);
      
      return newIdToken;
    } catch (error) {
      console.error('[AuthService] Échec du rafraîchissement:', error);
      throw error;
    }
  },

  isAuthenticated: () => {
    return !!auth.currentUser || !!cookieService.getUserId();
  },

  getCustomToken: async () => {
    return "FAKE_CUSTOM_TOKEN";
  },

  silentRefresh: async () => {
    try {
      if (!auth.currentUser) return null;
      await authService.refreshToken();
      return "FAKE_CUSTOM_TOKEN";
    } catch (error) {
      console.error('[AuthService] Silent refresh failed:', error);
      cookieService.clearAll();
      return null;
    }
  },

  async getTermsInfo() {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/terms-info`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch terms info');
      return await response.json();
    } catch (error) {
      console.error('Error fetching terms info:', error);
      return null;
    }
  },

  async acceptTerms(version) {
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/accept-terms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ version }),
      });
      if (!response.ok) throw new Error('Failed to accept terms');
      return await response.json();
    } catch (error) {
      console.error('Error accepting terms:', error);
      throw error;
    }
  },
};
